/**
 * ChunkManager - handles streaming terrain chunks as the camera moves
 * Migrated from test.html prototype
 */

import * as THREE from 'three';
import { Chunk } from './Chunk';
import { TerrainDecorator } from './TerrainDecorator';
import { getTerritoryColor, tileHeight } from './TerrainGenerator';
import type { GameConfig, FactionId } from '@/types';
import { DEFAULT_CONFIG } from '@/types';

export class ChunkManager {
  private chunks: Map<string, Chunk> = new Map();
  private lastCenter = { cx: Infinity, cz: Infinity };
  private scene: THREE.Scene;
  private config: GameConfig;
  private decorator: TerrainDecorator;
  private highlightedFaction: FactionId | null = null;

  constructor(scene: THREE.Scene, config: GameConfig = DEFAULT_CONFIG) {
    this.scene = scene;
    this.config = config;
    this.decorator = new TerrainDecorator(scene);
  }

  private ensureChunk(cx: number, cz: number): void {
    const key = `${cx},${cz}`;
    if (!this.chunks.has(key)) {
      const chunk = new Chunk(cx, cz, this.config);
      this.chunks.set(key, chunk);
      this.scene.add(chunk.group);
      // Add trees to this chunk
      this.decorator.processChunk(cx, cz, this.config.chunkSize, this.config.tileSize);
    }
  }

  private dropChunk(cx: number, cz: number): void {
    const key = `${cx},${cz}`;
    const chunk = this.chunks.get(key);
    if (!chunk) return;

    this.scene.remove(chunk.group);
    chunk.dispose();
    this.chunks.delete(key);
    // Remove trees from this chunk
    this.decorator.removeChunk(cx, cz, this.config.chunkSize, this.config.tileSize);
  }

  update(cameraX: number, cameraZ: number): void {
    const { chunkSize, tileSize, viewDistance } = this.config;

    const cx = Math.floor(cameraX / (chunkSize * tileSize));
    const cz = Math.floor(cameraZ / (chunkSize * tileSize));

    if (cx === this.lastCenter.cx && cz === this.lastCenter.cz) {
      return;
    }

    this.lastCenter = { cx, cz };

    // Determine which chunks should be loaded
    const keep = new Set<string>();

    for (let dz = -viewDistance; dz <= viewDistance; dz++) {
      for (let dx = -viewDistance; dx <= viewDistance; dx++) {
        const x = cx + dx;
        const z = cz + dz;
        keep.add(`${x},${z}`);
        this.ensureChunk(x, z);
      }
    }

    // Remove chunks that are out of range
    for (const key of this.chunks.keys()) {
      if (!keep.has(key)) {
        const [x, z] = key.split(',').map(Number);
        this.dropChunk(x, z);
      }
    }
  }

  getChunkAt(wx: number, wz: number): Chunk | null {
    const { chunkSize, tileSize } = this.config;
    const cx = Math.floor(wx / (chunkSize * tileSize));
    const cz = Math.floor(wz / (chunkSize * tileSize));
    return this.chunks.get(`${cx},${cz}`) || null;
  }

  getTileAt(wx: number, wz: number): {
    tileX: number;
    tileZ: number;
    worldX: number;
    worldZ: number;
    height: number;
    isLand: boolean;
    isBeach: boolean;
  } | null {
    const chunk = this.getChunkAt(wx, wz);
    return chunk?.getTileAt(wx, wz) || null;
  }

  updateTileColor(wx: number, wz: number, color: THREE.Color): void {
    const chunk = this.getChunkAt(wx, wz);
    chunk?.updateTileColor(wx, wz, color);
  }

  /**
   * Update territory color for a tile based on faction ownership
   */
  updateTerritoryColor(tileX: number, tileZ: number, factionId: FactionId | null): void {
    const { tileSize } = this.config;
    // Convert tile coordinates to world coordinates (center of tile)
    const wx = (tileX + 0.5) * tileSize;
    const wz = (tileZ + 0.5) * tileSize;

    const chunk = this.getChunkAt(wx, wz);
    if (!chunk) return;

    const h = tileHeight(wx, wz);
    let color = getTerritoryColor(wx, wz, h, factionId);

    // Apply highlight effect if this faction is highlighted
    if (this.highlightedFaction && factionId === this.highlightedFaction) {
      // Make highlighted territory brighter and more saturated
      color = color.clone();
      color.offsetHSL(0, 0.1, 0.15);
    } else if (this.highlightedFaction && factionId !== this.highlightedFaction) {
      // Dim non-highlighted territories
      color = color.clone();
      color.offsetHSL(0, -0.15, -0.1);
    }

    chunk.updateTileColor(wx, wz, color);
  }

  /**
   * Set the faction to highlight on the map
   * Pass null to clear highlighting
   */
  setHighlightedFaction(factionId: FactionId | null): void {
    this.highlightedFaction = factionId;
  }

  /**
   * Get the currently highlighted faction
   */
  getHighlightedFaction(): FactionId | null {
    return this.highlightedFaction;
  }

  /**
   * Update territory colors for multiple tiles (batch update)
   */
  updateTerritoryColors(tiles: Array<{ x: number; z: number; factionId: FactionId | null }>): void {
    for (const tile of tiles) {
      this.updateTerritoryColor(tile.x, tile.z, tile.factionId);
    }
  }

  getLoadedChunkCount(): number {
    return this.chunks.size;
  }

  clear(): void {
    for (const [key] of this.chunks) {
      const [x, z] = key.split(',').map(Number);
      this.dropChunk(x, z);
    }
    this.lastCenter = { cx: Infinity, cz: Infinity };
    this.decorator.dispose();
  }

  getTreeCount(): number {
    return this.decorator.getTreeCount();
  }
}
